import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export type AppThemeMode = "light" | "dark";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private readonly storageKey = "flowdrizzle.themeMode";
  private readonly themeSubject = new BehaviorSubject<AppThemeMode>(this.getInitialTheme());
  private globalThemeEnabled = false;

  readonly theme$ = this.themeSubject.asObservable();

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.applyTheme(this.themeSubject.value);
  }

  get currentTheme(): AppThemeMode {
    return this.themeSubject.value;
  }

  setTheme(theme: AppThemeMode): void {
    localStorage.setItem(this.storageKey, theme);
    this.themeSubject.next(theme);
    this.applyTheme(theme);
  }

  toggleTheme(): void {
    this.setTheme(this.currentTheme === "dark" ? "light" : "dark");
  }

  setGlobalThemeEnabled(enabled: boolean): void {
    this.globalThemeEnabled = enabled;
    this.applyTheme(this.themeSubject.value);
  }

  private getInitialTheme(): AppThemeMode {
    const storedTheme = localStorage.getItem(this.storageKey);
    return storedTheme === "dark" ? "dark" : "light";
  }

  private applyTheme(theme: AppThemeMode): void {
    const isDark = this.globalThemeEnabled && theme === "dark";
    this.document.documentElement.classList.toggle("fd-dark-mode", isDark);
    this.document.documentElement.classList.toggle("fd-light-mode", !isDark);
    this.document.body.classList.toggle("fd-dark-mode", isDark);
    this.document.body.classList.toggle("fd-light-mode", !isDark);
  }
}
